// File-import bridge.
//
// Reads a File (from drag-drop or <input type=file>), routes to the
// right parser, then persists into web_scripts + web_drafts +
// web_script_versions so it lands on the welcome list the same as
// any web-created script. Falls back to plain in-memory load when
// Supabase isn't configured (so the dev fallback path still works).

import { parseAny } from '../parsers/index.js';
import { setActiveScriptId } from './active-script.js';

function extensionOf(name) {
  const m = /\.[^.]+$/.exec(name || '');
  return m ? m[0].toLowerCase() : '';
}

async function readAsText(file) {
  return await file.text();
}

async function readAsBytes(file) {
  return new Uint8Array(await file.arrayBuffer());
}

async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function currentUserId(supabase) {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
}

// Serialize a parsed Script back to Fountain text so the persisted
// version row has the canonical body. Imported FDX/PDF round-trip
// into Fountain so subsequent edits use the editor's native model.
function scriptToFountain(script) {
  const out = [];
  // Title page block.
  const tp = script.titlePage || {};
  const keys = ['title', 'credit', 'author', 'source', 'draft_date', 'date', 'contact', 'notes'];
  for (const k of keys) {
    if (tp[k]) out.push(`${k[0].toUpperCase() + k.slice(1)}: ${tp[k]}`);
  }
  if (out.length) out.push('');

  // Prelude.
  for (const b of script.prelude || []) {
    out.push(serializeBlock(b));
    out.push('');
  }
  // Scenes.
  for (const scene of script.scenes || []) {
    if (scene.heading) {
      out.push(scene.heading);
      out.push('');
    }
    for (const b of scene.blocks || []) {
      out.push(serializeBlock(b));
      out.push('');
    }
  }
  return out.join('\n').trim() + '\n';
}

function serializeBlock(b) {
  if (!b) return '';
  switch (b.type) {
    case 'character':
      return b.text.toUpperCase();
    case 'parenthetical':
      return b.text.startsWith('(') ? b.text : `(${b.text})`;
    case 'transition':
      return b.text.startsWith('>') ? b.text : b.text.toUpperCase();
    case 'centered':
      return b.text.startsWith('>') ? b.text : `> ${b.text} <`;
    case 'scene-heading':
      return /^(INT|EXT|INT\/EXT|I\/E)\./i.test(b.text) ? b.text : `.${b.text}`;
    default:
      return b.text;
  }
}

export function buildImportHandlers({ supabase, bus }) {
  async function importFile(file) {
    if (!file) return { ok: false, error: 'no file' };
    const name = file.name || 'Untitled';
    const ext = extensionOf(name);
    if (!['.fountain', '.fdx', '.pdf', '.txt'].includes(ext)) {
      return { ok: false, error: `unsupported file type: ${ext || '(none)'}` };
    }

    let parsed;
    try {
      if (ext === '.pdf') {
        const bytes = await readAsBytes(file);
        parsed = await parseAny({ bytes, extension: ext, filePath: name });
      } else {
        const text = await readAsText(file);
        parsed = await parseAny({ text, extension: ext, filePath: name });
      }
    } catch (err) {
      return { ok: false, error: err?.message || 'parse failed' };
    }

    const fountain = scriptToFountain(parsed);
    const title = parsed.title || name.replace(/\.[^.]+$/, '');

    // No Supabase / signed-out: just hand the parsed script to the
    // editor in-memory. Round-trip won't persist on reload.
    if (!supabase) {
      const inMem = { ...parsed, filePath: `import:${name}`, title };
      setActiveScriptId(inMem.filePath);
      bus.emit('script:loaded', inMem);
      return { ok: true, ephemeral: true };
    }
    const userId = await currentUserId(supabase);
    if (!userId) {
      const inMem = { ...parsed, filePath: `import:${name}`, title };
      setActiveScriptId(inMem.filePath);
      bus.emit('script:loaded', inMem);
      return { ok: true, ephemeral: true };
    }

    // Persist as a new web_scripts row + main draft + initial version.
    try {
      const { data: script, error: sErr } = await supabase
        .from('web_scripts')
        .insert({ user_id: userId, title, format: 'fountain' })
        .select()
        .single();
      if (sErr) throw sErr;
      const { data: draft, error: dErr } = await supabase
        .from('web_drafts')
        .insert({
          user_id: userId,
          script_id: script.id,
          name: 'main',
          display_name: 'Main',
        })
        .select()
        .single();
      if (dErr) throw dErr;
      await supabase.from('web_scripts').update({ active_draft_id: draft.id }).eq('id', script.id);

      const sha = await sha256Hex(fountain);
      await supabase.from('web_script_versions').insert({
        user_id: userId,
        script_id: script.id,
        draft_id: draft.id,
        content: fountain,
        content_sha256: sha,
        label: `imported from ${ext.slice(1).toUpperCase()}`,
        manual: true,
        word_count: fountain.split(/\s+/).filter(Boolean).length,
        byte_count: fountain.length,
      });
      // Reload through the canonical loader so the editor mounts the
      // freshly-persisted version (matches openScript shape).
      const loaded = { ...parsed, filePath: script.id, title: script.title };
      setActiveScriptId(script.id);
      bus.emit('script:loaded', loaded);
      return { ok: true, path: script.id };
    } catch (err) {
      return { ok: false, error: err?.message || 'import failed' };
    }
  }

  return { importFile };
}
